import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const OrderFilters = ({ onFiltersChange, totalOrders }) => {
  const [filters, setFilters] = useState({
    searchQuery: '',
    dateRange: 'all',
    status: 'all',
    paymentStatus: 'all',
    serviceCategory: 'all',
    paymentMethod: 'all'
  });

  const [showAdvanced, setShowAdvanced] = useState(false);

  const statusOptions = [
    { value: 'all', label: 'All Statuses' },
    { value: 'completed', label: 'Completed' },
    { value: 'in-progress', label: 'In Progress' },
    { value: 'pending', label: 'Pending' },
    { value: 'cancelled', label: 'Cancelled' },
    { value: 'shipped', label: 'Shipped' }
  ];

  const paymentStatusOptions = [
    { value: 'all', label: 'All Payment Status' },
    { value: 'paid', label: 'Paid' },
    { value: 'pending', label: 'Pending' },
    { value: 'failed', label: 'Failed' },
    { value: 'refunded', label: 'Refunded' }
  ];

  const serviceCategoryOptions = [
    { value: 'all', label: 'All Categories' },
    { value: 'consultancy', label: 'Consultancy' },
    { value: 'infrastructure', label: 'Infrastructure' },
    { value: 'networking', label: 'Networking' },
    { value: 'software', label: 'Software' },
    { value: 'policy', label: 'Policy' },
    { value: 'strategy', label: 'Strategy' },
    { value: 'integration', label: 'Integration' }
  ];

  const paymentMethodOptions = [
    { value: 'all', label: 'All Payment Methods' },
    { value: 'mobile money', label: 'Mobile Money' },
    { value: 'credit card', label: 'Credit Card' },
    { value: 'bank transfer', label: 'Bank Transfer' },
    { value: 'cash', label: 'Cash' }
  ];

  const dateRangeOptions = [
    { value: 'all', label: 'All Time' },
    { value: 'today', label: 'Today' },
    { value: 'week', label: 'This Week' },
    { value: 'month', label: 'This Month' },
    { value: 'quarter', label: 'This Quarter' },
    { value: 'year', label: 'This Year' }
  ];

  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    onFiltersChange(newFilters);
  };

  const handleClearFilters = () => {
    const clearedFilters = {
      searchQuery: '',
      dateRange: 'all',
      status: 'all',
      paymentStatus: 'all',
      serviceCategory: 'all',
      paymentMethod: 'all'
    };
    setFilters(clearedFilters);
    onFiltersChange(clearedFilters);
  };

  const hasActiveFilters = Object.values(filters)?.some(value => value !== 'all' && value !== '');

  return (
    <div className="bg-card border border-border rounded-lg p-6 mb-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-2">
          <Icon name="Filter" size={20} className="text-muted-foreground" />
          <h3 className="text-lg font-semibold text-foreground">Filter Orders</h3>
          <span className="text-sm text-muted-foreground">({totalOrders} orders)</span>
        </div>
        <div className="flex items-center space-x-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowAdvanced(!showAdvanced)}
            iconName={showAdvanced ? "ChevronUp" : "ChevronDown"}
            iconPosition="right"
          >
            Advanced Filters
          </Button>
          {hasActiveFilters && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleClearFilters}
              iconName="X"
              iconPosition="left"
            >
              Clear All
            </Button>
          )}
        </div>
      </div>
      {/* Basic Filters */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
        <Input
          type="search"
          placeholder="Search by order number..."
          value={filters?.searchQuery}
          onChange={(e) => handleFilterChange('searchQuery', e?.target?.value)}
          className="w-full"
        />
        
        <Select
          options={dateRangeOptions}
          value={filters?.dateRange}
          onChange={(value) => handleFilterChange('dateRange', value)}
          placeholder="Select date range"
        />
        
        <Select
          options={statusOptions}
          value={filters?.status}
          onChange={(value) => handleFilterChange('status', value)}
          placeholder="Select status"
        />
        
        <Select
          options={serviceCategoryOptions}
          value={filters?.serviceCategory}
          onChange={(value) => handleFilterChange('serviceCategory', value)}
          placeholder="Select category"
        />
      </div>
      {/* Advanced Filters */}
      {showAdvanced && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t border-border">
          <Select
            options={paymentStatusOptions}
            value={filters?.paymentStatus}
            onChange={(value) => handleFilterChange('paymentStatus', value)}
            placeholder="Select payment status"
          />
          
          <Select
            options={paymentMethodOptions}
            value={filters?.paymentMethod}
            onChange={(value) => handleFilterChange('paymentMethod', value)}
            placeholder="Select payment method"
          />
        </div>
      )}
      {/* Active Filters Display */}
      {hasActiveFilters && (
        <div className="flex flex-wrap items-center gap-2 mt-4 pt-4 border-t border-border">
          <span className="text-sm text-muted-foreground">Active filters:</span>
          {filters?.searchQuery && (
            <span className="inline-flex items-center space-x-1 px-2 py-1 bg-primary/10 text-primary text-xs rounded-full">
              <span>Search: "{filters?.searchQuery}"</span>
              <button onClick={() => handleFilterChange('searchQuery', '')}>
                <Icon name="X" size={12} />
              </button>
            </span>
          )}
          {filters?.dateRange !== 'all' && (
            <span className="inline-flex items-center space-x-1 px-2 py-1 bg-primary/10 text-primary text-xs rounded-full">
              <span>{dateRangeOptions?.find(opt => opt?.value === filters?.dateRange)?.label}</span>
              <button onClick={() => handleFilterChange('dateRange', 'all')}>
                <Icon name="X" size={12} />
              </button>
            </span>
          )}
          {filters?.status !== 'all' && (
            <span className="inline-flex items-center space-x-1 px-2 py-1 bg-primary/10 text-primary text-xs rounded-full">
              <span>{statusOptions?.find(opt => opt?.value === filters?.status)?.label}</span>
              <button onClick={() => handleFilterChange('status', 'all')}>
                <Icon name="X" size={12} />
              </button>
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default OrderFilters;